import React, { useState, useEffect } from 'react';
import { FaAndroid, FaApple, FaRobot, FaInfoCircle, } from 'react-icons/fa';
import ApiService from '../models/ApiService';
import { FaPencil } from 'react-icons/fa6';

function Setting() {
    // State for toggles - using correct parameter names from API
    const [androidAds, setAndroidAds] = useState(true);
    const [iosAds, setIosAds] = useState(false);
    const [aiMode, setAiMode] = useState(true);
    const [aiDisableReason, setAiDisableReason] = useState('');

    // UI states
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [activeToggle, setActiveToggle] = useState(null);

    // State for reason modal
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [newReason, setNewReason] = useState('');
    const [isUpdatingReason, setIsUpdatingReason] = useState(false);

    // Fetch settings on component mount
    useEffect(() => {
        fetchSettings();
    }, []);

    // Fetch settings from API
    const fetchSettings = async () => {
        setIsFetching(true);
        setError(null);
        try {
            const response = await ApiService.GetSettings();
            console.log("Settings response:", response);

            if (response.data && response.data.responseContent) {
                const settings = response.data.responseContent;
                // Use correct parameter names from API response
                setAndroidAds(settings.android_ads ?? true);
                setIosAds(settings.ios_ads ?? false);
                setAiMode(settings.ai_mode ?? true);

                // FIX: The reason is directly in responseContent as 'reason'
                // Not nested inside another object
                setAiDisableReason(settings.ai_disable_reason || '');

                console.log("Setting reason to:", settings.reason); // Debug log
            }
        } catch (error) {
            console.error("Failed to fetch settings:", error);
            setError("Failed to load settings. Please refresh the page.");
        } finally {
            setIsFetching(false);
        }
    };

    // Save Android toggle to API
    const saveAndroidToggle = async (newValue) => {
        setActiveToggle('android');
        setIsLoading(true);
        setError(null);

        try {
            const payload = {
                android_ads: newValue
            };

            console.log("Saving Android toggle:", payload);
            const response = await ApiService.ToggleAndroid(payload);

            if (response.status === 200) {
                setSuccessMessage(response.data.message || "Android ads setting updated!");
                setTimeout(() => setSuccessMessage(''), 2000);
            }
        } catch (error) {
            console.error("Failed to save Android setting:", error);
            setError("Failed to update Android ads. Please try again.");
            // Revert the toggle on error
            setAndroidAds(!newValue);
        } finally {
            setIsLoading(false);
            setActiveToggle(null);
        }
    };

    // Save iOS toggle to API
    const saveIosToggle = async (newValue) => {
        setActiveToggle('ios');
        setIsLoading(true);
        setError(null);

        try {
            const payload = {
                ios_ads: newValue
            };

            console.log("Saving iOS toggle:", payload);
            const response = await ApiService.ToggleIos(payload);

            if (response.status === 200) {
                setSuccessMessage(response.data.message || "iOS ads setting updated!");
                setTimeout(() => setSuccessMessage(''), 2000);
            }
        } catch (error) {
            console.error("Failed to save iOS setting:", error);
            setError("Failed to update iOS ads. Please try again.");
            // Revert the toggle on error
            setIosAds(!newValue);
        } finally {
            setIsLoading(false);
            setActiveToggle(null);
        }
    };

    // Save AI Mode toggle to API
    const saveAiModeToggle = async (newValue) => {
        setActiveToggle('ai');
        setIsLoading(true);
        setError(null);

        try {
            const payload = {
                ai_mode: newValue
            };

            console.log("Saving AI Mode toggle:", payload);
            const response = await ApiService.ToggleAiMode(payload);

            if (response.status === 200) {
                // Update disable reason based on response
                if (response.data && response.data.responseContent) {
                    // Fix: The API might return 'reason' field
                    // setAiDisableReason(response.data.responseContent.reason || '');
                }
                setSuccessMessage(response.data.message || "AI mode setting updated!");
                setTimeout(() => setSuccessMessage(''), 2000);
            }
        } catch (error) {
            console.error("Failed to save AI Mode setting:", error);
            setError("Failed to update AI mode. Please try again.");
            // Revert the toggle on error
            setAiMode(!newValue);
        } finally {
            setIsLoading(false);
            setActiveToggle(null);
        }
    };

    // Update AI disable reason
    const updateAiDisableReason = async () => {
        if (!newReason.trim()) {
            setError("Please enter a reason");
            return;
        }

        setIsUpdatingReason(true);
        setError(null);

        try {
            // Fix: Payload should be { "reason": "text" }
            const payload = {
                reason: newReason
            };

            console.log("Updating AI disable reason:", payload);
            const response = await ApiService.UpdateAiDisableReason(payload);

            if (response.status === 200) {
                // setAiDisableReason(newReason);
                setSuccessMessage(response.data.message || "AI disable reason updated!");
                fetchSettings();
                setShowReasonModal(false);
                setNewReason('');
                setTimeout(() => setSuccessMessage(''), 2000);
            }
        } catch (error) {
            console.error("Failed to update AI disable reason:", error);
            setError("Failed to update AI disable reason. Please try again.");
        } finally {
            setIsUpdatingReason(false);
        }
    };

    // Handle toggle changes with auto-save
    const handleToggle = (type) => {
        // Clear any previous messages when user makes changes
        setError(null);
        setSuccessMessage('');

        switch (type) {
            case 'android': {
                const newValue = !androidAds;
                setAndroidAds(newValue);
                saveAndroidToggle(newValue);
                break;
            }
            case 'ios': {
                const newValue = !iosAds;
                setIosAds(newValue);
                saveIosToggle(newValue);
                break;
            }
            case 'ai': {
                const newValue = !aiMode;
                setAiMode(newValue);
                saveAiModeToggle(newValue);
                break;
            }
            default:
                break;
        }
    };

    // Open reason modal
    const openReasonModal = () => {
        setNewReason(aiDisableReason);
        setShowReasonModal(true);
    };

    // Show loading skeleton while fetching
    if (isFetching) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="mb-8">
                    <div className="h-8 w-32 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((item) => (
                        <div key={item} className="bg-white rounded-xl shadow-lg border border-[--border-color-g] overflow-hidden">
                            <div className="p-6">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="p-3 bg-gray-200 rounded-lg animate-pulse h-14 w-14"></div>
                                    <div className="flex-1">
                                        <div className="h-5 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
                                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                    <div>
                                        <div className="h-4 w-16 bg-gray-200 rounded animate-pulse mb-1"></div>
                                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                                    </div>
                                    <div className="h-6 w-11 bg-gray-200 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-[--text-color] mb-2">
                    Settings
                </h1>
                <p className="text-gray-600">
                    Manage your application settings and preferences
                </p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span>{error}</span>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{successMessage}</span>
                    </div>
                </div>
            )}

            {/* AI Disable Reason Banner - Always shows when AI is disabled, regardless of reason */}
            {!aiMode && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start justify-between gap-2 text-yellow-700">
                        <div className="flex items-start gap-2">
                            <FaInfoCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                            <div>
                                <span className="font-medium">AI Mode Disabled</span>
                                {aiDisableReason ? (
                                    <p className="text-sm mt-1">{aiDisableReason}</p>
                                ) : (
                                    <p className="text-sm mt-1 text-yellow-600 italic">
                                        No reason provided. Click the edit button to add a reason.
                                    </p>
                                )}
                            </div>
                        </div>
                        <button
                            onClick={openReasonModal}
                            className="p-1 hover:bg-yellow-100 rounded transition-colors"
                            title={aiDisableReason ? "Edit reason" : "Add reason"}
                        >
                            <FaPencil className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Settings Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Android Ads Card */}
                <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-[--border-color-g] overflow-hidden">
                    <div className="p-6">
                        {/* Icon and Title */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-[--bg-color] rounded-lg">
                                <FaAndroid className="text-3xl text-[--text-color]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-[--text-color-g]">
                                    Android Ads
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Manage Android advertisement settings
                                </p>
                            </div>
                        </div>

                        {/* Status and Toggle */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Status</p>
                                <p className={`text-sm font-semibold ${androidAds ? 'text-green-600' : 'text-red-600'}`}>
                                    {androidAds ? 'Active' : 'Inactive'}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {activeToggle === 'android' && (
                                    <svg className="animate-spin h-4 w-4 text-[--primary-btn-color]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                <button
                                    onClick={() => handleToggle('android')}
                                    disabled={isLoading && activeToggle === 'android'}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[--primary-btn-color] focus:ring-offset-2 ${androidAds ? 'bg-green-600' : 'bg-gray-300'
                                        } ${isLoading && activeToggle === 'android' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${androidAds ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* iOS Ads Card */}
                <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-[--border-color-g] overflow-hidden">
                    <div className="p-6">
                        {/* Icon and Title */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-[--bg-color] rounded-lg">
                                <FaApple className="text-3xl text-[--text-color]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-[--text-color-g]">
                                    iOS Ads
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Manage iOS advertisement settings
                                </p>
                            </div>
                        </div>

                        {/* Status and Toggle */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Status</p>
                                <p className={`text-sm font-semibold ${iosAds ? 'text-green-600' : 'text-red-600'}`}>
                                    {iosAds ? 'Active' : 'Inactive'}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {activeToggle === 'ios' && (
                                    <svg className="animate-spin h-4 w-4 text-[--primary-btn-color]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                <button
                                    onClick={() => handleToggle('ios')}
                                    disabled={isLoading && activeToggle === 'ios'}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[--primary-btn-color] focus:ring-offset-2 ${iosAds ? 'bg-green-600' : 'bg-gray-300'
                                        } ${isLoading && activeToggle === 'ios' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${iosAds ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* AI Mode Card */}
                <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-[--border-color-g] overflow-hidden">
                    <div className="p-6">
                        {/* Icon and Title */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="p-3 bg-[--bg-color] rounded-lg">
                                <FaRobot className="text-3xl text-[--text-color]" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-[--text-color-g]">
                                    AI Mode
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Manage AI-powered settings
                                </p>
                            </div>
                        </div>

                        {/* Status and Toggle */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Status</p>
                                <p className={`text-sm font-semibold ${aiMode ? 'text-green-600' : 'text-red-600'}`}>
                                    {aiMode ? 'Active' : 'Inactive'}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {activeToggle === 'ai' && (
                                    <svg className="animate-spin h-4 w-4 text-[--primary-btn-color]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                <button
                                    onClick={() => handleToggle('ai')}
                                    disabled={isLoading && activeToggle === 'ai'}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[--primary-btn-color] focus:ring-offset-2 ${aiMode ? 'bg-green-600' : 'bg-gray-300'
                                        } ${isLoading && activeToggle === 'ai' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${aiMode ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Auto-save indicator */}
            {!isFetching && !error && (
                <div className="mt-6 text-center">
                    <p className="text-xs text-gray-500">
                        Settings are auto-saved when toggled
                    </p>
                </div>
            )}

            {/* AI Disable Reason Modal */}
            {showReasonModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-2xl font-bold text-[--text-color-g] mb-4">
                                {aiDisableReason ? 'Edit' : 'Add'} AI Disable Reason
                            </h2>
                            <p className="text-sm text-gray-600 mb-4">
                                This reason will be displayed when AI mode is disabled.
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason
                                </label>
                                <textarea
                                    value={newReason}
                                    onChange={(e) => setNewReason(e.target.value)}
                                    placeholder="Enter reason for disabling AI mode..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[--primary-btn-color] focus:border-transparent"
                                    rows="4"
                                    disabled={isUpdatingReason}
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowReasonModal(false);
                                        setNewReason('');
                                        setError(null);
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    disabled={isUpdatingReason}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={updateAiDisableReason}
                                    disabled={isUpdatingReason || !newReason.trim()}
                                    className="px-4 py-2 bg-[--primary-btn-color] hover:bg-[--secondary-btn-color] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isUpdatingReason ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Saving...</span>
                                        </>
                                    ) : (
                                        'Save Reason'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Setting;